package com.vizio.redwolf.consent.client

import com.vizio.redwolf.consent.model.ConsentV2DataRequestBody
import com.vizio.redwolf.consent.model.SaveUserConsentV2ProxyRequestInfo
import com.vizio.redwolf.consent.model.SaveUserConsentV2RequestBody
import com.vizio.redwolf.consent.model.UserConsentV2DataRequestInfo
import com.vizio.redwolf.consent.model.UserConsentV2ProxyStatusRequestInfo
import com.vizio.redwolf.consent.model.UserConsentV2StatusRequestBody
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.ConsentV2FusedEnvironment
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.applyUserBearerToken
import com.vizio.redwolf.utils.setTraceId
import com.vizio.redwolf.utils.safeHttpCall
import io.ktor.client.HttpClient
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.encodeToJsonElement

class ConsentV2FusedHttpClient(private val consentEnv: ConsentV2FusedEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) : ConsentV2FusedClient {
    private val httpClient: HttpClient = clientConfig.clientProvider.httpClient
    private val consentApiKey = consentEnv.apiKey
    private val consentPath = consentEnv.envPath
    private val json = Json { encodeDefaults = true }

    override suspend fun getConsentData(requestInfo: UserConsentV2DataRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(
            ConsentV2DataRequestBody(
                consentIds = requestInfo.consentIds,
                consentGroupIds = requestInfo.consentGroupIds
            )
        )
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = consentEnv.host
                    path("/get-consent-data")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(HttpHeaders.AcceptLanguage, requestInfo.acceptLanguage)
                    append(MESSAGE_FORMAT_HEADER, requestInfo.consentMessageFormat)
                    append(CONSENT_API_KEY_HEADER, consentApiKey)
                }
                setBody(requestBody)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun getUserConsentStatus(requestInfo: UserConsentV2ProxyStatusRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(
            UserConsentV2StatusRequestBody(
                userId = requestInfo.userId,
                userIdType = requestInfo.userIdType,
                consentIds = requestInfo.consentIds,
                consentGroupIds = requestInfo.consentGroupIds
            )
        )
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = consentEnv.proxyHost
                    path("$consentPath/get-user-consent")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(HEADER_API_VERSION, consentEnv.apiVersion)
                }
                setBody(requestBody)
                applyUserBearerToken(requestInfo.accessToken)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun saveUserConsent(requestInfo: SaveUserConsentV2ProxyRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(
            SaveUserConsentV2RequestBody(
                userId = requestInfo.userId,
                userIdType = requestInfo.userIdType,
                consentIdsActions = requestInfo.consentIdsActions,
                consentGroupIdsActions = requestInfo.consentGroupIdsActions,
                consentToken = requestInfo.consentToken
            )
        )
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = consentEnv.proxyHost
                    path("$consentPath/save-user-consent")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    //TODO Remove accept language when fixed from CMS side/workaround for issue is setting it as empty https://vizioirvine.atlassian.net/browse/RED-1046
                    append(HttpHeaders.AcceptLanguage, "")
                    append(HEADER_API_VERSION, consentEnv.apiVersion)
                }
                setBody(requestBody)
                applyUserBearerToken(requestInfo.accessToken)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    companion object {
        private const val HEADER_API_VERSION = "api-version"
        private const val CONTENT_TYPE_APP_JSON_UTF8 = "application/json"
        private const val MESSAGE_FORMAT_HEADER = "Consent-Format"
        private const val CONSENT_API_KEY_HEADER = "api-key"
    }
}